import { useEffect, useState } from 'react';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, EYEBROW, OUTLINE_CONTROL, type ThemeClasses } from './cmsTheme.ts';
import { cdnVariant, cdnSrcSet, cdnSquareUrl } from '../../lib/cdn.ts';
import { getSpotifyLinkUrl } from '../../application/festivalSelectors.ts';
import type { Artist, PageSectionTheme } from '../../lib/types.ts';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ArtistsBlockProps {
  artists: readonly Artist[];
  /** Section heading. Defaults to 'Årets navne' (the artists list). */
  heading?: string;
  /** Which side the images sit on (desktop). Stacked image-on-top on mobile either way.
   *  Defaults to 'left'. */
  imageSide?: 'left' | 'right';
  /** Section theme, so the rows stay legible on dark section backgrounds
   *  (green/plum). Defaults to 'ice'. */
  theme?: PageSectionTheme;
}

// ---------------------------------------------------------------------------
// Custom-event typing for the `mf:select-artist` channel — the page-local
// mechanism other sections use to expand + scroll to a given artist's card.
// Dispatched today by ScheduleBlock when a schedule entry is selected; the
// same channel also serves cross-section artist deep-linking.
// ---------------------------------------------------------------------------

interface SelectArtistDetail {
  artistId: string;
}

// ---------------------------------------------------------------------------
// ArtistsBlock — heading + a chronological list of program acts, without an
// outer section/background. Serves both the announced artists ("Årets navne")
// and the features/"Oplevelser" list: the caller passes the chosen list, its
// heading, and which side the images sit on. Shared by CmsArtistsSection (CMS
// theme wrapper); strangler step CMS-034.
//
// v2: the rows are separated by the combination hairline, the day/scene pair
// dropped from a display-size coral list to a small accent label, and the
// Spotify link became an outline pill — the brand green was the loudest color
// in the section and it belonged to someone else.
// ---------------------------------------------------------------------------

export function ArtistsBlock({ artists, heading = 'Årets navne', imageSide = 'left', theme = 'ice' }: ArtistsBlockProps) {
  const t = themeOf(theme);

  // Listen for cross-section "select this artist" requests (e.g. from a
  // timetable entry on the same page) and scroll to the matching card. Cards
  // are always fully shown now, so there is nothing to expand — just scroll.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SelectArtistDetail>).detail;
      if (!detail?.artistId) return;
      const el = document.getElementById(`artist-${detail.artistId}`);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    window.addEventListener('mf:select-artist', handler);
    return () => window.removeEventListener('mf:select-artist', handler);
  }, []);

  return (
    <>
      <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>
        {heading}
      </SectionHeading>

      <div>
        {artists.map(artist => (
          <ArtistCard key={artist.id} artist={artist} imageSide={imageSide} t={t} />
        ))}
      </div>
    </>
  );
}

/* ---------- ArtistCard — static, fully-shown act row ----------
   No expand/collapse and no card chrome: each act renders its full bio + a
   "Hør på Spotify" link (replacing the old embedded player), separated from the
   previous one by the section hairline. Image on the requested side on
   desktop; stacked image-on-top on mobile.
*/
interface ArtistCardProps {
  artist: Artist;
  imageSide: 'left' | 'right';
  t: ThemeClasses;
}

function ArtistCard({ artist, imageSide, t }: ArtistCardProps) {
  const spotifyUrl = getSpotifyLinkUrl(artist);
  const dag = artist.festivalDayName?.trim() || '—';
  const scene = artist.stageName?.trim() || '—';
  // A library artist's square crop is published by the BackOffice (`images.square`, one width) and
  // used exactly as given — no srcset, since no other width was published. Without it (the 2026
  // artists, or a crop not generated yet) the card derives a square URL from the 3:2 one: that only
  // exists for pictures still carrying the retired fixed rendition matrix, so when it 404s fall
  // back to the 3:2 card crop, which object-cover then centres in the square.
  const [squareFailed, setSquareFailed] = useState(false);
  const publishedSquare = artist.images?.square;
  const squareImage = squareFailed ? artist.images?.desktop : cdnSquareUrl(artist.images?.desktop);
  const onRight = imageSide === 'right';

  return (
    <article
      id={`artist-${artist.id}`}
      className={`scroll-mt-32 border-t first:border-t-0 ${t.line}`}
    >
      <div
        className={`grid items-start gap-6 md:gap-10 py-8 md:py-10 ${
          onRight ? 'md:grid-cols-[1fr_minmax(0,26rem)]' : 'md:grid-cols-[minmax(0,26rem)_1fr]'
        }`}
      >
        {/* Image (DOM-first so it stacks on top on mobile; ordered to the right on desktop when requested) */}
        <div className={`aspect-square w-full rounded-card overflow-hidden ${t.img} ${onRight ? 'md:order-2' : ''}`}>
          {publishedSquare ? (
            <img
              src={publishedSquare}
              alt={artist.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : squareImage ? (
            <img
              key={squareImage}
              src={cdnVariant(squareImage, 'mobile')}
              srcSet={cdnSrcSet(squareImage) || undefined}
              sizes="(min-width: 768px) 416px, 100vw"
              alt={artist.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={squareFailed ? undefined : () => setSquareFailed(true)}
            />
          ) : (
            <div className="w-full h-full img-placeholder flex items-center justify-center">
              <span className={`font-display text-3xl ${t.body}`}>♪</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`min-w-0 ${onRight ? 'md:order-1' : ''}`}>
          <h3 className={`font-display leading-tight tracking-[-0.02em] text-[22px] md:text-2xl ${t.heading}`}>
            {artist.name}
          </h3>

          <dl className={`mt-2 flex flex-wrap gap-x-5 gap-y-1 ${EYEBROW} ${t.eyebrow}`}>
            <div className="flex gap-1.5">
              <dt>Dag:</dt>
              <dd>{dag}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt>Scene:</dt>
              <dd>{scene}</dd>
            </div>
          </dl>

          {artist.bioHtml && (
            <div
              className={`mt-4 font-normal leading-relaxed text-sm md:text-base [&_p]:mb-3 [&>*:last-child]:mb-0 ${t.body}`}
              style={{ textWrap: 'pretty' }}
              dangerouslySetInnerHTML={{ __html: artist.bioHtml }}
            />
          )}

          {spotifyUrl && (
            <div className="mt-5">
              <a
                href={spotifyUrl}
                target="_blank"
                rel="noreferrer noopener"
                className={`inline-flex items-center gap-2 rounded-full pl-3 pr-5 py-2.5 font-medium transition-colors ${OUTLINE_CONTROL[t.outline]}`}
              >
                <SpotifyGlyph />
                Hør på Spotify
              </a>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function SpotifyGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.622.622 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.13-9.965-1.166a.779.779 0 1 1-.452-1.491c3.632-1.102 8.147-.568 11.232 1.329a.78.78 0 0 1 .257 1.071zm.105-2.835c-3.223-1.914-8.54-2.09-11.617-1.156a.935.935 0 1 1-.542-1.79c3.532-1.072 9.404-.865 13.115 1.338a.935.935 0 0 1-.956 1.608z" />
    </svg>
  );
}
